import { Columns3 } from "lucide-react";
import {
  SipocParamsSchema,
  defaultSipocParams,
  type SipocParams,
} from "@playbookai/widget-schemas";
import { registerWidget } from "../registry.js";
import type { SqlWidgetDef } from "../types.js";
import { SipocWidget } from "./Component.js";
import { SipocCard } from "./Card.js";
import { SipocExpandedEditor } from "./ExpandedEditor.js";

/**
 * SIPOC — the VSM scoping board (VSM-WS9, PB-346). Registered as a first-class
 * widget-kind (schema + registry + data-blind Component) so it can later be
 * placed on the VSM current-state canvas and AI-drafted.
 *
 * `modes: []` — register-only. The widget registry has no module-ownership
 * discriminator yet, and SOLVE's canvas catalog treats every SQL canvas kind as
 * its own; giving SIPOC `["canvas"]` today would leak it into the SOLVE `+
 * Widget` menu / catalog-parity test. PB-197 introduces the VSM canvas host (and
 * the discriminator it needs) and flips this to `["canvas"]`.
 */
export const sipocDef: SqlWidgetDef<SipocParams> = {
  kind: "sipoc",
  label: "SIPOC",
  modes: [],
  singleton: true,
  defaultSize: { w: 880, h: 320 },
  defaultParams: defaultSipocParams,
  Component: SipocWidget,
  // The canvas card mounts the SAME editable board at the `lg` scale (opt-in
  // host seam): the artifact card is 2000×920 flow units read at canvas zoom,
  // so panel-scale type is unreadable there, and a facilitator edits steps in
  // place rather than through the expand button.
  CardComponent: SipocCard,
  // Registry seam, not another case in `expanded-editors.tsx` (invariant 3). A SIPOC
  // outgrows a card fast — past ~3 process steps it scrolls inside the card's height.
  expandable: true,
  ExpandedComponent: SipocExpandedEditor,
  storage: "sql",
  schema: SipocParamsSchema,
  accent: "purple",
  icon: Columns3,
  subtitle: "Suppliers · Inputs · Process · Outputs · Customers",
  defaultRoster: false,
  category: "single",
  layout: { col: "full", row: 1, width: 880, height: 320 },
};

registerWidget(sipocDef);
